import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { practiceApi, errorDetailText } from '../../services/api';
import { Task } from '../../types';
import { dayLabel, isOverdue } from './format';

const TYPE_LABELS: Record<string, string> = {
  drafting: 'Drafting',
  research: 'Research',
  enquiry: 'Enquiry',
  argument_prep: 'Argument prep',
  document_verification: 'Document verification',
  filing: 'Filing',
  other: 'Task',
};

/**
 * One task row: checkbox toggles done via the API, title, case link, due date
 * (red when overdue) and the counterpart's name on assigned/delegated tasks.
 */
export function TaskRow({
  task,
  onChanged,
}: {
  task: Task;
  /** Called with the updated task after a successful status toggle. */
  onChanged?: (task: Task) => void;
}) {
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const done = task.status === 'done';
  const overdue = !done && isOverdue(task.due_date);

  const toggle = async () => {
    if (isBusy) return;
    setIsBusy(true);
    setError('');
    try {
      const { data } = await practiceApi.updateTask(task.id, {
        status: done ? 'open' : 'done',
      });
      onChanged?.(data);
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not update the task.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="p-3">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={toggle}
          disabled={isBusy}
          aria-pressed={done}
          aria-label={done ? `Reopen "${task.title}"` : `Mark "${task.title}" done`}
          className={`shrink-0 mt-0.5 h-6 w-6 rounded-md border flex items-center justify-center transition-colors ${
            done
              ? 'bg-emerald-500 border-emerald-500 text-white'
              : 'border-legal-300 text-transparent hover:border-emerald-400'
          }`}
        >
          {isBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-legal-400" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <p className={`text-sm break-words ${done ? 'text-legal-400 line-through' : 'text-legal-900'}`}>
            {task.title}
          </p>
          <p className="text-xs text-legal-400 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span>{TYPE_LABELS[task.task_type] ?? task.task_type}</span>
            {task.matter_id && task.matter_title && (
              <Link
                to={`/app/cases/${task.matter_id}`}
                className="hover:text-accent-700 transition-colors truncate max-w-[14rem]"
              >
                {task.matter_title}
              </Link>
            )}
            {task.due_date && (
              <span className={overdue ? 'text-red-600 font-medium' : ''}>
                Due {dayLabel(task.due_date)}
              </span>
            )}
            {task.assignee_name && <span>for {task.assignee_name}</span>}
            {task.owner_name && <span>from {task.owner_name}</span>}
          </p>
        </div>
      </div>
      {error && <p className="text-xs text-red-600 mt-1.5 ml-9">{error}</p>}
    </div>
  );
}
